// src/components/AppBar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TodayIcon from '@mui/icons-material/Today';
import Logout from '@mui/icons-material/Logout';
import Person from '@mui/icons-material/Person';
import Email from '@mui/icons-material/Email';
import Divider from '@mui/material/Divider';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import Paper from '@mui/material/Paper';
import { getCurrentLocation } from './navbarLinks';
import useCustomToast from '../hooks/useToast';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

// Styled components for breadcrumb
const BreadcrumbContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(30),
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    // backgroundColor: theme.palette.action.main,
    backdropFilter: 'blur(4px)',
    minHeight: 40,
    transition: 'all 0.3s ease',
    // '&:hover': {
    //     backgroundColor: theme.palette.action.secondary,
    // },
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const MainLocationChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.action.main,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.85rem',
    '& .MuiChip-icon': {
        color: '#fff',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.secondary,
    },
}));

const SubLocationChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.action.main,
    color: '#fff',
    fontWeight: 400,
    fontSize: '0.8rem',
    '& .MuiChip-icon': {
        color: 'rgba(255, 255, 255, 0.8)',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.secondary,
    },
}));

const LocationIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

export default function CustomAppBar({ open, toggleDrawer }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const theme = useTheme();
    const [currentDate, setCurrentDate] = useState('');
    const [currentLocation, setCurrentLocation] = useState({
        main: 'Dashboard',
        mainIcon: null,
        sub: null,
        subIcon: null,
        description: 'Overview & Analytics',
        parentDescription: null
    });
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Get user info from sessionStorage
        const userData = sessionStorage.getItem('userInfo');
        if (userData) {
            setUserInfo(JSON.parse(userData));
        }

        // Set current date
        const today = new Date();
        setCurrentDate(today.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }));
    }, []);

    // Update current location when route changes
    useEffect(() => {
        const locationInfo = getCurrentLocation(location.pathname);
        setCurrentLocation(locationInfo);
    }, [location.pathname]);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleClose();
        
        showToast("Warning", "You Have Logged Out From The System", "warning");
        
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('token');
        
        setTimeout(() => {
            navigate('/');
            window.location.reload();
        }, 500);
    };

    return (
        <AppBar position="absolute" open={open}>
            <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    onClick={toggleDrawer}
                    sx={{
                        marginRight: '16px',
                        ...(open && { display: 'none' }),
                    }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Breadcrumb Navigation */}
                <BreadcrumbContainer>
                    <LocationIndicator>
                        {/* Main Location */}
                        {currentLocation.mainIcon && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                                {React.cloneElement(currentLocation.mainIcon, { 
                                    sx: { fontSize: 20, color: 'rgba(255,255,255,0.9)' } 
                                })}
                            </Box>
                        )}
                        
                        <MainLocationChip
                            label={currentLocation.main}
                            size="small"
                            icon={<HomeIcon sx={{ fontSize: 16 }} />}
                            onClick={() => navigate('/dashboard')}
                        />
                        <NavigateNextIcon sx={{ fontSize: 20, fontWeight:'bold', color: 'white' }} />
                        {currentLocation.sub && (
                            <>
                                <SubLocationChip
                                    label={currentLocation.sub}
                                    size="small"
                                    icon={currentLocation.subIcon ? 
                                        React.cloneElement(currentLocation.subIcon, { sx: { fontSize: 16 } }) : 
                                        null
                                    }
                                />
                            </>
                        )}
                        <NavigateNextIcon sx={{ fontSize: 20, fontWeight:'bold', color: 'white' }} />
                        {/* Description */}
                        {currentLocation.description && (
                            <Box 
                                sx={{ 
                                    ml: 1, 
                                    px: 1.5, 
                                    py: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: theme.palette.action.main,
                                    border: `1px solid ${theme.palette.action.main}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'default',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.secondary,
                                        borderColor: theme.palette.action.secondary,
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px rgba(255, 101, 0, 0.25)`,
                                    },
                                }}
                            >
                                <Typography variant="caption" sx={{ 
                                    color: 'white',
                                    fontWeight: 400,
                                    fontSize: '0.7rem',
                                    lineHeight: 1.4,
                                    letterSpacing: '0.3px',
                                }}>
                                    {currentLocation.description}
                                </Typography>
                            </Box>
                        )}
                    </LocationIndicator>
                </BreadcrumbContainer>

                {/* Today's Date */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 2 }}>
                    <TodayIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                    <Typography variant="body2">
                        {currentDate}
                    </Typography>
                </Box>

                {/* User Info */}
                {userInfo && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                            icon={<AccountCircle color='white' />}
                            label={userInfo.name}
                            variant="outlined"
                            onClick={handleMenu}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        />

                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorEl}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            PaperProps={{
                                sx: {
                                    mt: 1.5,
                                    minWidth: 200,
                                }
                            }}
                        >
                            <Box sx={{ px: 2, py: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    User Information
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 0.5 }}>
                                    <Person sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                        {userInfo.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Email sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2">
                                        {userInfo.email}
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider />
                            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                                <Logout sx={{ mr: 1.5, fontSize: '1.25rem' }} />
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Toolbar>
        </AppBar>
    );
}